import './paginated-table-screen.scss';
import {Box, Button} from '@mui/material';
import {ReactElement} from 'react';
import {Link} from 'react-router-dom';
import {ContentPage} from '../content-page/content-page';
import {PageHeader} from '../page-header/page-header';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {PaginatedTableColumns} from '../../interfaces/paginated-table-columns';
import {usePaginatedTable} from '../../hooks/paginated-table';

/**
 * Screen that combines both the usePaginatedTable hook and PaginatedTable component.
 */
export const PaginatedTableScreen = (props: {
  emptyTableMessage: string;
  tableColumns: PaginatedTableColumns;
  fetchData: (
    limit: number,
    skip: number,
    search?: string
  ) => Promise<{
    totalCount: number;
    data: PaginatedTableData<any>;
  }>;
  avatar?: {
    type: 'user' | 'entity' | 'other' | 'entity-service-icon';
    isIcon?: boolean;
  };
  pageHeader?: {
    screenTitle?: string;
    searchLabel?: string;
    rightButton?: {
      label: string;
      toLink: string;
      endIcon: ReactElement;
      disabled?: boolean;
    };
  };
  linkableRows?: boolean;
  collapsibleRows?: boolean;
}) => {
  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentSearch,
    currentPage,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
  } = usePaginatedTable<any>({
    fetchData: props.fetchData,
  });

  return (
    <ContentPage>
      <Box sx={{width: '100%'}}>
        {props.pageHeader && (
          <PageHeader
            title={props.pageHeader.screenTitle}
            rightButtons={
              props.pageHeader.rightButton ? (
                !props.pageHeader.rightButton.disabled ? (
                  <Link
                    className="paginated-table-add-button-link"
                    to={props.pageHeader.rightButton.toLink}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      endIcon={props.pageHeader.rightButton.endIcon}
                    >
                      {props.pageHeader.rightButton.label}
                    </Button>
                  </Link>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={props.pageHeader.rightButton.endIcon}
                    disabled
                  >
                    {props.pageHeader.rightButton.label}
                  </Button>
                )
              ) : undefined
            }
            showSearch={true}
            searchLabel={props.pageHeader.searchLabel}
            searchText={currentSearch}
            searchOnChange={(text: string) => {
              // Update text search
              handleUpdateCurrentSearch(text);
            }}
            searchOnSubmit={() => {
              // Query backend about this
              handleApplyFilters(1, currentSearch);
            }}
            disableSearch={isLoading}
          />
        )}
        <PaginatedTable
          avatar={props.avatar}
          emptyTableMessage={props.emptyTableMessage}
          columns={props.tableColumns}
          rows={data}
          isLoading={isLoading}
          error={error}
          pagesCount={pagesCount}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          linkableRows={props.linkableRows}
          collapsibleRows={props.collapsibleRows}
        />
      </Box>
    </ContentPage>
  );
};
